import { Search } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import styles from "./SearchBar.module.css"

interface Props {
  value: string
  onChange: (value: string) => void
}

export function SearchBar({ value, onChange }: Props) {
  const [local, setLocal] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)
  const isFocusedRef = useRef(false)
  const isComposingRef = useRef(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Sync external resets (URL/back button) but never clobber in-progress typing.
  useEffect(() => {
    if (!isFocusedRef.current) {
      setLocal(value)
    }
  }, [value])

  function scheduleCommit(v: string) {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => onChange(v), 300)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value
    setLocal(v)
    if (!isComposingRef.current) {
      scheduleCommit(v)
    }
  }

  function handleCompositionStart() {
    isComposingRef.current = true
  }

  function handleCompositionEnd(e: React.CompositionEvent<HTMLInputElement>) {
    isComposingRef.current = false
    const v = e.currentTarget.value
    setLocal(v)
    scheduleCommit(v)
  }

  function handleFocus() {
    isFocusedRef.current = true
  }

  function handleBlur() {
    isFocusedRef.current = false
    const el = inputRef.current
    const valueBefore = local

    // iOS QuickType commits suggestion text asynchronously after blur.
    window.setTimeout(() => {
      const v = el?.value ?? valueBefore
      setLocal(v)
      clearTimeout(timer.current)
      onChange(v)

      // Suggestion insert blurs the field — refocus so search can continue.
      if (v !== valueBefore) {
        el?.focus()
      }
    }, 10)
  }

  function handleClear() {
    clearTimeout(timer.current)
    setLocal("")
    onChange("")
    inputRef.current?.focus()
  }

  return (
    <div className={styles.wrapper}>
      <Search className={styles.icon} size={16} aria-hidden />
      <input
        ref={inputRef}
        type="text"
        inputMode="search"
        enterKeyHint="search"
        className={styles.input}
        placeholder="会社名・証券コードで検索..."
        value={local}
        onChange={handleChange}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        onFocus={handleFocus}
        onBlur={handleBlur}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        data-1p-ignore
        data-lpignore="true"
      />
      <button
        type="button"
        className={`${styles.clearBtn} ${local ? styles.clearBtnVisible : ""}`}
        aria-label="検索をクリア"
        onClick={handleClear}
        tabIndex={local ? 0 : -1}
        aria-hidden={!local}
      >
        ×
      </button>
    </div>
  )
}
